import React from "react";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";
import { TransactionStatus } from "@bus/models/dist/enums/new/transaction-status.enum";
import { Main, Spinner } from "@vwfs-bronson/bronson-react";
import { ErrorInformation, ErrorCode } from "./common/ErrorInformation";
import { SessionContext } from "./services/api/Session";
import { isHeycarDealer, isVtiBOP, OriginSystem } from "./common/JourneyType";
import { savePersonalDetailsFullAppForm } from "./services/api/storeData/savePersonalDetails";
import { savePaymentDetails } from "./services/api/storeData/savePaymentDetails";
import { saveEmploymentDetails } from "./services/api/storeData/saveEmploymentDetails";
import { saveContactDetailsFullAppForm } from "./services/api/storeData/saveContactDetailsFullAppForm";
import { saveExpenditureDetails } from "./services/api/storeData/saveExpenditureDetails";
import { checkStatus } from "./services/api/finishFullAppForm";
import { startFullApplication } from "./services/api/startFullApplication";
import { checkMail } from "./services/api/loadDocuments/TlsCheck/api";

import { CustomerInfoProvider } from "./components/CustomerInfoDocs";
import { StorefrontQuery } from "./components/StorefrontQuery";
import { Footer, BackToTop, Header } from "./components/DefaultLayout";
import { HealthCheck, Maintenance } from "./components/HealthCheck";
import { Route as AppRoute } from "./common/Navigation";
import { ProductModifier } from "@busdk/storefront-service/dist/models/enums";
import { redirectAccordingToStatus } from "./common/continueBasedOnStatus";
import { getEmptyPrefillObject } from "./common/PrefillMapping/PrefillMapping";
import { NavigationBlockedModal } from "./common/NavigationBlockedModal";
import { SessionDataHandling } from "./common/SessionDataHandling";
import { CheckoutSelectPage } from "./pages/CheckOutSelectPage/CheckOutSelect";
import { Output } from "./Text";
import { startApplication } from "./services/api/storeData/storeBasicAppFormData";
import { ShoppingCart } from "./components/ShoppingCart/ShoppingCart";
import { BasicAppFormPage } from "./pages/BasicAppForm/BasicAppFormPage";
import PersonalDetailsSection from "./pages/BasicAppForm/PersonalDetailsSection";
import { savePersonalDetails } from "./services/api/storeData/savePersonalDetails";
import Licenses from "./components/Licenses";
import { AuthenticationCodeRequestPage } from "./pages/AuthenticationCodeRequestPage";
import { DataProtectionPage } from "./pages/DataProtectionsPage/DataProtectionPage";
import { TermsAndConditionsPage } from "./pages/TermsAndConditionsPage/TermsAndConditionsPage";
import { CreditResultPage } from "./pages/CreditResultPage/CreditResultPage";
import AcceptanceEsign from "./components/AcceptanceEsign/AcceptanceEsign";
import { DataForm } from "./components/DataForm";
import { SaveButton } from "./components/SaveButton";
import { PersonalDetailsSectionFull } from "./pages/FullApplicationFormPage/PersonalDetailsSection";
import { ContactDetailsSectionFull } from "./pages/FullApplicationFormPage/ContactDetailsSection";
import { EmploymentDetailsSectionFull } from "./pages/FullApplicationFormPage/EmploymentDetailsSection";
import { ExpenditureDetailsSectionFull } from "./pages/FullApplicationFormPage/ExpenditureDetailsSection";
import { PaymentSection } from "./pages/Sections";
import { SummaryPage } from "./pages/SummaryPage/index";
import CheckoutDacPage from "./pages/checkout-dac/CheckoutDacPage";
import { AdditionalAppFormPage } from "./pages/AdditionalAppForm/AdditionalAppFormPage";

import { NavigationRoute } from "./components/NavigationRoute";
import { CustomerSupportModal } from "./components/CustomerSupportModal";
import { DocumentUpload } from "./pages/DocumentUploadPage/DocumentUploadPage";
import { saveCustomerDocuments, saveCustomerDocumentsStatus } from "./services/api/storeData/saveCustomerDocuments";
import { VehicleUnavailableError } from "./common/VehicleUnavailableError";
import { DocumentUploadSuccessPage } from "./pages/DocumentUploadPage/DocumentUploadSuccessPage";
import { IdentFailed, IdentityPending } from "./components/Ident";
import { Optional } from "./common/Optional";
import { Analytics } from "./services/analytics";
import { setBreakPoint } from "./services/analytics/functions";
import { DEFAULT_DATALAYER } from "./services/analytics/datalayer";
import { pushStorefrontDataToDatalayer } from "./services/analytics/storefront";
import validateIban from "./services/api/iban/iban";
import BoniRefused from "./components/BoniCases/BoniRefused";
import BoniSuccessDealer from "./components/BoniCases/BoniSuccessDealer";
import BoniSuccessUCC from "./components/BoniCases/BoniSuccessUCC";
import BoniLanding from "./components/BoniCases/BoniLanding";
import { get } from "lodash";
import availabilityCheck from './services/api/availabilityCheck';
import SignatureSuccessPage from './pages/SignatureSuccessPage/SignatureSuccessPage';
import { SaveCustomerDocumentsState } from './common/types/enums';
import { loadStoredDocuments } from './services/api/loadDocuments';
import { expiredSessionLogic } from './ExpiredSession/ExpiredSessionLogic';
import { ExpiredSessionModal } from './ExpiredSession/ExpiredSessionModal';
import { sendContinueMail } from "./services/api/sendContinueLaterMail";
import { ExpiredSessionPage } from './ExpiredSession/ExpiredSessionPage';
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import {
  continueLaterLocalization,
  ContinueLater,
} from "@vwfs-its/sf-sac-frontend";
import { getActualPage, trackInteractionSuccess } from './pages/page.utils';
import * as moment from "moment";
class App extends React.Component {
  customerSupportModalRef = React.createRef();

  setLocalization = () => {
    const localization = continueLaterLocalization({
      locale: "de",
      brandName: process.env.REACT_APP_BRAND,
      dataProtectionLink: "https://www.vwfs.it/privacypolicies.html",
    });

    i18n
      .use(initReactI18next) // passes i18n down to react-i18next
      .init({
        resources: {},
        lng: "de",
        keySeparator: ".", // we do not use keys in form messages.welcome
        interpolation: {
          escapeValue: false, // react already safes from xss
        },
      });
    i18n.addResourceBundle("de", "sf-sac", localization, true, true);
  };

  constructor(props) {
    super(props);
    this.setLocalization();
    Analytics.setDefaults(DEFAULT_DATALAYER);
    this.onUnload = this.onUnload.bind(this);
    this.onUnload2 = this.onUnload2.bind(this);
    this.customerInfoProvider = new CustomerInfoProvider();

    /**
     * Concept for state management and data handling
     * - /prefill route with transactionId called:
     *   - load data, redirect to TAN auth page if status >INITIALIZED
     *   - load full data after TAN auth
     *
     * - other routes called, prefill data used to prefill form data
     * - page refresh means state and prefill data will be empty
     *   - loading data from FES after refreshing the page would be an option, but security/TAN auth would be needed.
     *     That means, we could simply re-use the existing prefill handling.
     *   - The only sensible option other then reloading with TAN would be updating the local data
     *     all the time. The problem is that the mapping between form values and customerData model is handled server-side.
     *     Therefore, the best way would be refreshing the local data from the server result whenever data on the server is updated.
     */
    this.state = {
      webCalc: undefined,
      transactionId: undefined,
      isDataLoaded: false,
      expired: false,
      dacSelected: false,
      abortModalShown: false,
      maintenanceMode: true,
      isSummary: false,
      showSupportModal: false,
      showContinueLaterModal: false,
      onDemand: false,
      showMailDispatchConfirmationModal: false,
      continueLaterProvided: false,
      showDebug: false,
      isJourneyDataInitialized: false,
      loading: false,
      uploading: false,
      pollingUrl: "",
      tlsCheck: {
        key: undefined,
        value: false,
      },
      basicAppForm: {
        isSavingData: false,
      },
      fullAppForm: {
        isSavingData: false,
      },
      summaryPage: {
        isSavingData: false,
      },
      identitySuccessUrl: "",
      prefill: {
        ...getEmptyPrefillObject(),
        //...this.getPrefillDataFromStorage(),
      },
      sessionData: {
        ...{
          transactionId: "",
          entryPointUrl: "",
          redirectUrl: "",
          token: "",
        },
        ...SessionDataHandling.getSessionDataFromStorage(),
      },
      identificationStarted: false
    };
  }

  onUnload(e) {
    const pagesWithSaveOnClose = [
      AppRoute.CheckoutSelect,
      AppRoute.BasicAppForm,
      AppRoute.FullApplicationForm,
      AppRoute.DocumentUpload,
      AppRoute.Dac,
      AppRoute.AdditionalAppForm,
    ];

    if (
      !this.state.continueLaterProvided &&
      pagesWithSaveOnClose.includes(window.location.pathname)
    ) {
      e.preventDefault();
      e.returnValue = true;
      this.setState({
        showContinueLaterModal: true,
        onDemand: false,
      });
    }
  }
  onUnload2(e) {
    this.setState({
      showContinueLaterModal: false,
    });
  }
  componentDidMount() {
    window.addEventListener("beforeunload", this.onUnload);
    window.addEventListener("unload", this.onUnload2);
    localStorage.setItem("actualPage", "");
    setBreakPoint();
  }
  componentWillUnmount() {
    window.removeEventListener("beforeunload", this.onUnload);
    window.removeEventListener("unload", this.onUnload2);
  }

  componentDidCatch(error, info) {
    /**
     * TODO: Error handling
     */
    // Example "componentStack":
    //   in ComponentThatThrows (created by App)
    //   in ErrorBoundary (created by App)
    //   in div (created by App)
    //   in App
    console.error("App.jsx componentDidCatch()", info.componentStack);
  }

  expiredSessionStart = (tokenExpiresAt) => {
    if (tokenExpiresAt) {
      expiredSessionLogic(tokenExpiresAt, () => this.setState({ showExpiredSessionModal: true }));
    }
  }

  storefrontQuerySuccess = (
    journeyData,
    paramTransactionId,
    productModifier,
    token,
    history
  ) => {
    Analytics.addToDatalayer({
      "dealerData.companyId": get(journeyData, "dealerData.companyId"),
      "dealerData.companyName": get(journeyData, "dealerData.companyName"),
      "dealerData.regionId": get(journeyData, "dealerData.regionId"),
      "dealerData.KVPS": get(journeyData, "dealerData.KVPS"),
      "dealerData.address.street": get(
        journeyData,
        "dealerData.address.street"
      ),
      "dealerData.address.zipCode": get(
        journeyData,
        "dealerData.address.zipCode"
      ),
      "dealerData.address.city": get(journeyData, "dealerData.address.city"),
      "dealerData.address.state": get(journeyData, "dealerData.address.state"),
      "core.attributes.context": get(journeyData, "salesChannel.originSystem"),
      "core.attributes.transactionID": get(journeyData, "transaction.id"),
    });
    /**
     * Data has been loaded successfully. There are two cases:
     * - Status INITIALIZED: Full data loaded, redirecting to first page of journey
     * - Status after INITIALIZED: Only subset of data is loaded, redirect to
     *   AuthenticationCodeRequestPage for TAN validation before loading full data.
     */

    journeyData.storefrontTransactionId = paramTransactionId;
    this.checkMailAddress(journeyData);
    if (journeyData.iban) {
      this.getBankData(journeyData.iban);
    }
    this.setState({
      isDataLoaded: true,
    });
  
    this.prefillDataLoadedSetState(
      journeyData,
      paramTransactionId,
      productModifier,
      token
    );

    /**
     * TODO: why load all data first if health check would redirect to maintenanceMode?
     * This handling should be moved.
     */
    if (this.state.maintenanceMode) {
      history.push("/maintenance");
      return;
    }

    this.expiredSessionStart(journeyData?.custom?.tokenExpiresAt, history);
    // Detect wheater only initial data is provided or full data (compare with buildTanRequiredResponse() function in proxy)
    if (journeyData.vehicleData) {
      // Full data was loaded (because token was valid)
      const targetRoute = redirectAccordingToStatus(
        journeyData.transaction.status,
        journeyData.salesChannel?.originSystem
      );
      history.push(targetRoute);
    } else {
      // TAN validation required
      history.push(AppRoute.AuthenticationCodeRequestPage);
    }
  };

  updateSessionData = (data) => {
    const updatedData = {
      ...SessionDataHandling.getSessionDataFromStorage(),
      ...data
    }
    SessionDataHandling.saveSessionDataToStorage(updatedData);
    this.setState({
      ...this.state,
      sessionData: updatedData,
    })
  };

  /**
   * Set state after receiving prefill data.
   * Prefill data can be either full data in case token was still valid, or can be only the minimal
   * set of data needed for the TAN request.
   * @param {*} journeyData
   */
  prefillDataLoadedSetState = (
    journeyData,
    transactionId,
    productModifier,
    token
  ) => {
    this.setState((prevState) => {
      const sessionData = {
        transactionId:
          journeyData.transaction?.id || prevState.sessionData?.transactionId,
        entryPointUrl:
          journeyData.salesChannel?.entryPointUrl ||
          prevState.sessionData?.entryPointUrl,
        redirectUrl:
          journeyData.salesChannel?.redirectUrl ||
          prevState.sessionData?.redirectUrl,
        token:
          token || journeyData.custom?.token || prevState.sessionData?.token,
      };
      SessionDataHandling.saveSessionDataToStorage(sessionData);
      pushStorefrontDataToDatalayer({
        ...getEmptyPrefillObject(),
        ...journeyData,
      });

      return {
        ...prevState,
        transactionId,
        productModifier,
        sessionData,
        dacSelected:
          journeyData.transaction?.status ===
          TransactionStatus.DAC_APPLICATION_STARTED,
        isJourneyDataInitialized: true,
        loading: false,
        prefill: {
          ...getEmptyPrefillObject(),
          ...journeyData,
        },
      };
    });
  };

  /**
   * Set state after loading full data.
   * @param {*} journeyData
   * @param {*} documentData
   * @param {*} history
   */
  prefillDataLoadedAfterAuthentication = (
    journeyData,
    documentData,
    history
  ) => {
    /**
     * The data has been successfully received and mapped
     */
    this.setState(
      (prevState) => {
        const sessionData = {
          ...prevState.sessionData,
          transactionId: journeyData.transaction.id,
          entryPointUrl: journeyData.salesChannel.entryPointUrl,
          redirectUrl: journeyData.salesChannel.redirectUrl,
          token: journeyData.custom?.token,
        };
        SessionDataHandling.saveSessionDataToStorage(sessionData);

        pushStorefrontDataToDatalayer({
          ...prevState.prefill, // keep previous data
          ...journeyData, // overwrite with new prefill data
          storefrontTransactionId: prevState.prefill.storefrontTransactionId,
        });

        return {
          ...prevState,
          sessionData,
          documents: documentData,
          prefill: {
            ...prevState.prefill, // keep previous data
            ...journeyData, // overwrite with new prefill data
            /**
             * TODO: validate and fix handling of storefrontTransactionId.
             * At the moment for HeyCar this is set to the transationId which does not make sense.
             * transaction.id <> transaction.storefrontTransactionId
             */
            storefrontTransactionId: prevState.prefill.storefrontTransactionId,
          },
          isJourneyDataInitialized: true,
        };
      },
      // Push history only after state change
      () =>
        history.push(
          redirectAccordingToStatus(
            journeyData.transaction.status,
            journeyData.salesChannel?.originSystem
          )
        )
    );
  };

  getBankData = (iban) => {
    return validateIban(iban).then((result) => {
      if (result.data.isValid) {
        this.setState({
          prefill: {
            ...this.state.prefill,
            bic: result.data.paymentData.bic,
            bankName: result.data.paymentData.bankName,
          },
        });
      }
    });
  };

  checkMailAddress = async (values) => {
    const { email } = values;
    if (!email) {
      return;
    }
    this.setState({ tlsCheck: { key: email, inProgress: true } });

    return checkMail(email)
      .then((result) => {
        this.setState({
          tlsCheck: { key: email, value: result.tlsSupported },
        });
      })
      .catch(() => {
        this.setState({
          tlsCheck: { key: email, value: false },
        });
      });
  };

  /**
   * Central error handling.
   *
   * @param error Error (usually axios.post error)
   * @param history React router history object
   */
  handleError = (
    error,
    history,
    errorCode = ErrorCode.Default,
    route = AppRoute.Error
  ) => {
    if (process.env.STAGE !== "prod") {
      console.error(error);
    }

    if (error) {
      if (this.handleUnauthorizedError(error, history)) {
        return;
      }
  
      if (this.handleVehicleUnavailableError(error, history)) {
        return;
      }
    }

    history.push(route, {
      errorCode: errorCode,
    });
  };

  /**
   *
   * @param error
   * @param history
   */
  handleVehicleUnavailableError = (error, history) => {
    /**
     * Handle the real error code plus a workaround for local development, when
     * serverless-offline produces produces 502 Bad Gateway instead.
     */
    if (
      error.response?.status === 410 ||
      (error.response?.status === 502 && error.response?.data?.errorMessage)
    ) {
      try {
        const errorPayload =
          error.response?.data?.payload ||
          JSON.parse(error.response?.data?.errorMessage).payload;

        const sessionData = {
          transactionId: this.state.sessionData?.transactionId,
          entryPointUrl: this.state.sessionData?.entryPointUrl,
          redirectUrl: errorPayload.redirectUrl,
          token: this.state.sessionData?.token,
        };

        this.setState({
          sessionData,
          expired: true,
          loading: false,
          prefill: {
            salesChannel: {
              originSystem: errorPayload.originSystem,
            },
          },
          isDataLoaded: true,
        });

        SessionDataHandling.saveSessionDataToStorage(sessionData);
        history.push(AppRoute.VehicleUnavailable, {
          redirectUrl: errorPayload.redirectUrl,
          originSystem: this.state?.prefill?.salesChannel?.originSystem,
        });

        return true;
      } catch (err) {
        console.error(err);
        return false;
      }
    }

    return false;
  };

  /**
   *
   * @param error
   * @param history
   */
  handleUnauthorizedError = (error, history) => {
    if (error.response?.status === 401) {
      try {
        // Go to TAN validation page to get new token, all necessary data should still be in state
        history.push(AppRoute.AuthenticationCodeRequestPage);
        return true;
      } catch (err) {
        console.error(err);
        return false;
      }
    }

    return false;
  };

  /**
   *
   * @param routeProps
   */
  redirectToEntryPointIfNeeded = (routeProps) => {
    if (this.state.isJourneyDataInitialized) {
      return;
    }
    this.checkIsVti(routeProps?.location?.pathname);

    const vtiRoutes = [
      `${AppRoute.DataProtection}/vti`,
      `${AppRoute.TermsAndConditionsPage}/vti`,
      `${AppRoute.Licenses}/vti`,
    ];

    const pagesWithoutJourneyData = [
      AppRoute.AuthenticationCodeRequestPage,
      AppRoute.Dac,
      AppRoute.DataProtection,
      AppRoute.Error,
      AppRoute.Esign,
      AppRoute.EsignApproved,
      AppRoute.EsignFailed,
      AppRoute.EsignPending,
      AppRoute.EsignRefused,
      AppRoute.EsignSuccess,
      AppRoute.Licenses,
      `${AppRoute.Licenses}/`, // for some strange reason, /licenses is redirected to /licenses/
      AppRoute.Maintenance,
      AppRoute.NoEsign,
      AppRoute.Prefill,
      AppRoute.Rejection,
      AppRoute.VehicleUnavailable,
      AppRoute.TermsAndConditionsPage,
      AppRoute.BoniRefused,
      AppRoute.BoniSuccessDealer,
      AppRoute.BoniSuccessUCC,
      ...vtiRoutes,
    ];

    if (pagesWithoutJourneyData.includes(routeProps.location.pathname)) {
      return;
    }

    if (this.state.sessionData?.entryPointUrl) {
      // Refresh by going to prefill page using entryPointUrl if available
      window.location.assign(this.state.sessionData.entryPointUrl);
    } else {
      // Session information is missing but page needs data, go to error
      routeProps.history.push(AppRoute.Error, {
        errorCode: ErrorCode.MissingJourneyData,
      });
    }
  };

  checkIsVti = (pathname) => {
    if (
      pathname.includes("/vti") &&
      !this.state?.prefill?.salesChannel?.originSystem
    ) {
      this.setState({
        isDataLoaded: true,
        prefill: {
          ...this.state?.prefill,
          salesChannel: {
            originSystem: OriginSystem.VTI_GIS,
          },
        },
      });
    }
    if (
      !this.state?.isDataLoaded &&
      (pathname === AppRoute.DataProtection ||
        pathname === AppRoute.Licenses ||
        pathname === AppRoute.TermsAndConditionsPage)
    ) {
      this.setState({ isDataLoaded: true });
    }
  };

  afterSummaryPage = (accepted, history) => {
    if (isHeycarDealer(this.state.prefill?.salesChannel?.originSystem)) {
      if (accepted) {
        history.push(AppRoute.BoniSuccessDealer);
      } else {
        history.push(AppRoute.BoniRefused);
      }
    } else {
      if (accepted) {
        history.push(AppRoute.BoniSuccessUCC);
      } else {
        history.push(AppRoute.BoniRefused);
      }
    }
  };
  trackCreditCheckProgress = () => {
    const pageData = {
      [Analytics.keys.pageName()]: "Credit check progress",
      [Analytics.keys.viewChange()]: "Credit check progress",
      [Analytics.keys.eventType(0)]: "pageView",
      [Analytics.keys.eventAction(0)]: "Success",
      [Analytics.keys.eventType(1)]: "FinanceApproval",
      [Analytics.keys.eventAction(1)]: "Request",
    };
    Analytics.setTrackingData(pageData);
    Analytics.trackPage();
  };

  handleSaveCustomerDocs = (history) => {
    const onError = (error) => {
      this.handleError(
        error,
        history,
        ErrorCode.FullApplicationFormDocumentUploadError
      );
    };

    const checkPolling = () => {
      const finishPoll = () => {
        clearInterval(timer);
        clearTimeout(timeoutTimer);
      }
  
      const checkStatus = (data) => {
        const status = data?.status
        switch (status) {
          case SaveCustomerDocumentsState.SUCCESS:
            finishPoll();
            history.push(AppRoute.DocumentUploadSuccess);
            break;
          case SaveCustomerDocumentsState.ERROR:
            finishPoll();
            onError();
            break;
          default:
            break;
        }
      }
  
      const executePoll = () => {
        if (!isPending) {
          isPending = true;
          saveCustomerDocumentsStatus(
            this.state.prefill?.storefrontTransactionId
          )
            .then(
              ({ data }) => {
                checkStatus(data);
              },
              error => {
                finishPoll();
                onError(error);
              }
            )
            .finally(() => {
              isPending = false;
            })
        }
      }
  
      const timeoutReached = () => {
        finishPoll()
        onError()
      }
  
      let isPending = false;
      const TIMEOUT = 250000;
      const WAIT_TIME = 3000;
      const timeoutTimer = setTimeout(() => timeoutReached(), TIMEOUT);
      const timer = setInterval(executePoll, WAIT_TIME);
    }

    saveCustomerDocuments(
      this.state.prefill?.storefrontTransactionId
    )
      .then(() => {
        checkPolling();
      })
      .catch((error) => {
        onError(error);
      });
  };

  setPrefilledDocuments = (documentData) => {
    this.setState(
      (prevState) => {
        return {
          ...prevState,
          documents: documentData,
          uploading: false,
        };
      }
    );
  }

  onLoadDocuments = async (history) => {
    try {
      this.setState({ uploading: true })
      const documentsResponse = await loadStoredDocuments(
        this.state.prefill?.storefrontTransactionId
      );
      this.setPrefilledDocuments(documentsResponse && documentsResponse.data);
    } catch (error) {
      this.handleError(
        error,
        history,
        ErrorCode.FullApplicationFormDocumentUploadError
      );
    }
  }

  setShowContinueLater = (value) => {
    this.setState({
      showContinueLaterModal: value,
    });
  };

  getDefaultSacTrackingData = () => ({
    [Analytics.keys.pageName()]: getActualPage(),
    [Analytics.keys.viewChange()]: `Save application form${ this.state?.onDemand
      ? ' on demand'
      : ' by advice'}`,
  });

  trackSaCShow = () => {
    const defaultTrackingData = this.getDefaultSacTrackingData();
    setTimeout(() => {
      const pageData = {
        [Analytics.keys.eventType(0)]: 'pageView',
        [Analytics.keys.eventAction(0)]: 'Success',
      }
      Analytics.setTrackingData({...defaultTrackingData, ...pageData})
      Analytics.trackPage()
    }, 1000)
  };

  trackSaCCancel = () => {
    const defaultTrackingData = this.getDefaultSacTrackingData();
    trackInteractionSuccess('Application form not saved', defaultTrackingData)
  };

  trackSaCSubmitted = () => {
    const defaultTrackingData = this.getDefaultSacTrackingData();
    setTimeout(() => {
      const pageData = {
        [Analytics.keys.eventType(0)]: 'Application data',
        [Analytics.keys.eventAction(0)]: 'Saved',
      }
      Analytics.setTrackingData({...defaultTrackingData, ...pageData})
      Analytics.trackPage()
    }, 1000)
  };

  getLinkValidity = () => {
    // New transactions have pre-calculated expiryDate
    if (this.state.prefill?.transaction?.expiryDate) {
      return moment(this.state.prefill?.transaction?.expiryDate);
    }
    // Fallback case for old transactions without expiryDate
    return moment(this.state.prefill?.transaction?.creationDate).add(
      30,
      "days"
    );
  };

  continueLaterSubmit = (values) => {
    const contactData = {
      mobilePhoneCountryCode: values?.continueLaterPhoneCountryCode,
      mobilePhoneNumber: values?.continueLaterPhone,
      email: values?.continueLaterEmail,
    }
    return sendContinueMail(this.state.sessionData.transactionId, contactData);
  };
  
  render() {
    const { loading, transactionId } = this.state;
    return (
      /*
        This attempts to merge both HashRouter functionality and add support for location state
        See https://github.com/ReactTraining/history/issues/435

        If this turns out to break for IE11 we will roll back.
      */
      <Router>
        {loading && <Spinner fullPage />}
        <Route
          render={({ history }) => {
            return (
              <>
                <ContinueLater
                  version={1}
                  productName={process.env.REACT_APP_PRODUCT.toUpperCase()}
                  email={this.state.summary?.email || this.state.prefill.email}
                  phoneCountryCode={
                    this.state?.summary?.mobilePhoneCountryCode ||
                    this.state.prefill.telephoneCountryCode ||
                    this.state.prefill.mobilePhoneCountryCode
                  }
                  phoneNo={
                    this.state?.summary?.mobilePhoneNumber ||
                    this.state.prefill.telephoneNumber ||
                    this.state.prefill.mobilePhoneNumber
                  }
                  dataProtection={true}
                  hideDataProtectionCheckbox={true}
                  dataModels={{}}
                  testid="continueLaterModal"
                  showModal={{
                    showContinueLater: this.state.showContinueLaterModal,
                    setShowContinueLater: this.setShowContinueLater,
                  }}
                  onShow={this.trackSaCShow}
                  onCancel={() => {
                    this.trackSaCCancel();
                    this.setShowContinueLater(false);
                  }}
                  onSendAgainSubmit={(values) => {
                    this.setState({ loading: true });
                    this.continueLaterSubmit(values)
                      .finally(() => {
                        this.setState({ loading: false });
                      })
                  }}
                  linkValidityDate={this.getLinkValidity()}
                  onContinueLaterSubmit={(
                    values,
                    showModal,
                    setShowMailDispatched,
                    transactionStatus
                  ) => {
                    this.setState({ loading: true });
                    this.continueLaterSubmit(values)
                      .then(() => {
                        this.setState({ continueLaterProvided: true, loading: false  });
                        this.trackSaCSubmitted();
                        setShowMailDispatched(true);
                      })
                      .catch((error) => {
                        this.handleError(error, history, ErrorCode.Default);
                      })
                      .finally(() => {
                        this.setShowContinueLater(false);
                      });
                  }}
                ></ContinueLater>
              </>
            );
          }}
        />
        <CustomerSupportModal
          ref={this.customerSupportModalRef}
          prefill={this.state.prefill}
        />
        <Route
          render={({ history }) => {
            return (
              <ExpiredSessionModal
                shown={this.state.showExpiredSessionModal}
                onConfirm={(data) => {
                  this.setState({ showExpiredSessionModal: false });
                  this.expiredSessionStart(data?.data?.tokenExpiresAt);
                }}
                onClose={() => {
                  this.setState({ showExpiredSessionModal: false });
                  sendContinueMail(
                    this.state?.sessionData?.transactionId || this.state.prefill.storefrontTransactionId || this.state.transactionId
                  );
                  history.push(AppRoute.ExpiredSession);
                }}
                transactionId={this.state?.sessionData?.transactionId || this.state?.prefill?.storefrontTransactionId || this.state.transactionId}
              />
            )
          }}
        />

        <Route component={NavigationBlockedModal} />
        <Route
          render={(routeProps) => this.redirectToEntryPointIfNeeded(routeProps)}
        />
        <Route
          path={AppRoute.Prefill}
          render={({ history }) => {
            /**
             * Main entry point for prefill and Save & Continue.
             *
             * Expect prefill url to look like this /prefill?id=c63b76e6-9949-4aa1-a890-c66c071b892e&token=73eaeb48-7321-4586-aed2-6cf98cf72d92
             * GFTDE-1182 TAN + Token handling as specified in Solution Design.
             *
             * @param history route props
             */
            const getParam = (name) =>
              new URL(document.location.toString()).searchParams.get(name);
            const paramTransactionId =
              getParam("cid") ||
              getParam("id") ||
              this.state.sessionData?.transactionId;
            const optionalToken =
              getParam("token") || this.state.sessionData?.token;
            const productModifier = ProductModifier.NEW;

            if (!paramTransactionId) {
              history.push(AppRoute.Error, {
                errorCode: ErrorCode.MissingIdParameter,
              });
            }

            return (
              <HealthCheck
                history={history}
                onHealth={() => this.setState({ maintenanceMode: false })}
              >
                <StorefrontQuery
                  transactionId={paramTransactionId}
                  token={optionalToken}
                  productModifier={productModifier}
                  onQuery={() => {
                    this.setState({ loading: true });
                  }}
                  onError={(error) => {
                    this.setState({ loading: false });

                    if (this.handleVehicleUnavailableError(error, history)) {
                      return;
                    }

                    if (error.response?.status === 404) {
                      this.setState({ expired: true, loading: false });
                      history.push(AppRoute.AuthenticationCodeRequestPage);
                      return;
                    }

                    history.push(AppRoute.Error, {
                      errorCode: ErrorCode.ItemNotFound,
                    });
                  }}
                  onSuccess={(data) =>
                    this.storefrontQuerySuccess(
                      data,
                      paramTransactionId,
                      productModifier,
                      optionalToken,
                      history
                    )
                  }
                />
              </HealthCheck>
            );
          }}
        />

        <SessionContext.Provider
          value={{
            transactionId,
            entryPointUrl:
              this.state.prefill?.transactionData?.salesChannel?.entryPointUrl,
          }}
        >
          <Header
            isVtiBOP={isVtiBOP(this.state.prefill?.salesChannel?.originSystem)}
            isDataLoaded={this.state?.isDataLoaded}
            title={""}
            actionHeading={Output.resolve(
              "pages.container.quickAccess.links.customerSupport.label"
            )}
            onContact={(e) => {
              e.preventDefault();
              this.customerSupportModalRef.current.show();
            }}
            abortModalShown={this.state.abortModalShown}
            setAbortModalShown={(value) => {
              this.setState({ abortModalShown: value });
            }}
            redirectUrl={
              this.state.prefill.salesChannel &&
              this.state.prefill.salesChannel.redirectUrl
            }
            transactionId={this.state.prefill.storefrontTransactionId}
            productModifier={
              this.state.prefill.vehicleData?.isUsed
                ? ProductModifier.USED
                : ProductModifier.NEW
            }
          />
          <Main>
            <Route
              render={(props) => {
                return (
                  <>
                    {!this.state.maintenanceMode && (
                      <NavigationRoute
                        originSystem={
                          this.state.prefill?.salesChannel?.originSystem
                        }
                        {...props}
                      />
                    )}
                  </>
                );
              }}
            />
            <Switch>
              <Route
                path={AppRoute.Maintenance}
                render={({ history }) => {
                  return <Maintenance currentState={this.state}></Maintenance>;
                }}
              ></Route>
              <Route
                path={AppRoute.AuthenticationCodeRequestPage}
                render={({ history }) => {
                  return (
                    <AuthenticationCodeRequestPage
                      prefill={this.state.prefill}
                      onTanSuccess={(prefillData, documentData) => {
                        /**
                         * The full data has been loaded successfully after TAN validation,
                         * redirect to page for current status.
                         */
                        this.prefillDataLoadedAfterAuthentication(
                          prefillData,
                          documentData,
                          history
                        );
                      }}
                      transactionId={this.state.prefill.storefrontTransactionId}
                      expired={this.state.expired}
                      originSystem={
                        this.state.prefill?.salesChannel?.originSystem
                      }
                    />
                  );
                }}
              />
              {/* CheckoutSelect is DEPRECATED */}
              <Route
                path={AppRoute.CheckoutSelect}
                render={({ history }) => {
                  return (
                    <>
                      <ShoppingCart
                        history={history}
                        prefill={this.state.prefill}
                        calculation={
                          this.state.prefill?.financialProduct?.calculation
                        }
                        additionalServices={
                          this.state.prefill?.financialProduct
                            ?.additionalServicesDescriptions
                            ? this.state.prefill.financialProduct
                                .additionalServicesDescriptions[0]
                            : undefined
                        }
                        vehicleData={this.state.prefill?.vehicleData}
                      />
                      <CheckoutSelectPage
                        onDAC={(callback) => {
                          // set status DAC_APPLICATION_STARTED
                          startApplication(
                            this.state.prefill.storefrontTransactionId
                          )
                            .then(() => {
                              this.setState({ dacSelected: true });
                              if (callback) {
                                callback();
                              }
                              history.push(AppRoute.BasicAppForm);
                            })
                            .catch((reason) => {
                              this.handleError(
                                reason,
                                history,
                                ErrorCode.BasicAppFormSave
                              );
                            });
                        }}
                        onManual={(callback) => {
                          // Send status update to proxy to trigger status change to MANUAL_APPLICATION_STARTED
                          startApplication(
                            this.state.prefill.storefrontTransactionId
                          )
                            .then(() => {
                              this.setState({ dacSelected: false });
                              if (callback) {
                                callback();
                              }
                              history.push(AppRoute.BasicAppForm);
                            })
                            .catch((reason) => {
                              this.handleError(
                                reason,
                                history,
                                ErrorCode.BasicAppFormSave
                              );
                            });
                        }}
                      />
                      <SaveButton
                        onClick={() => {
                          this.setState({
                            showContinueLaterModal: true,
                            onDemand: true,
                          });
                        }}
                      />
                    </>
                  );
                }}
              />
              {/* BasicAppForm is DEPRECATED */}
              <Route
                path={AppRoute.BasicAppForm}
                render={({ history }) => {
                  return (
                    <>
                      <ShoppingCart
                        history={history}
                        prefill={this.state.prefill}
                        calculation={
                          this.state.prefill?.financialProduct?.calculation
                        }
                        vehicleData={this.state.prefill?.vehicleData}
                      />
                      <BasicAppFormPage
                        tlsCheckResult={this.state.tlsCheck.value}
                        isLoading={
                          this.state.tlsCheck.inProgress ||
                          this.state.basicAppForm.isSavingData
                        }
                        initialValues={this.state.summary || this.state.prefill}
                        initialSection={0}
                        onSubmit={(values, bag, callback) => {
                          this.setState({ summary: values });
                          // Submit form for both cases
                          startApplication(values.storefrontTransactionId)
                            .then(() => {
                              if (callback) {
                                callback();
                              }
                              if (this.state.dacSelected) {
                                history.push(AppRoute.Dac);
                              } else {
                                // manual (non-dac) flow, go to AdditionalAppForm
                                history.push(AppRoute.AdditionalAppForm);
                              }
                            })
                            .catch((reason) => {
                              this.handleError(
                                reason,
                                history,
                                ErrorCode.BasicAppFormSave
                              );
                            });
                        }}
                        onSectionSubmit={({ values }) =>
                          this.setState({ summary: values })
                        }
                      >
                        <PersonalDetailsSection
                          title={Output.resolve(
                            "pages.leasing.basicAppForm.personalDetails.title"
                          )}
                          subtitle={Output.resolve(
                            "pages.leasing.basicAppForm.personalDetails.subtitle"
                          )}
                          onSectionSubmit={(formValues) => {
                            this.setState({
                              basicAppForm: { isSavingData: true },
                            });
                            const transactionId =
                              this.state.prefill.storefrontTransactionId;
                            savePersonalDetails(transactionId, formValues)
                              .catch((reason) => {
                                this.handleError(
                                  reason,
                                  history,
                                  ErrorCode.BasicAppFormSavePersonalDetails
                                );
                              })
                              .finally(() => {
                                this.setState({
                                  basicAppForm: { isSavingData: false },
                                });
                                this.checkMailAddress(formValues);
                              });
                          }}
                        />
                      </BasicAppFormPage>
                      <SaveButton
                        onClick={() => {
                          this.setState({
                            showContinueLaterModal: true,
                            onDemand: true,
                          });
                        }}
                      />
                    </>
                  );
                }}
              />
              <Route
                path={AppRoute.FullApplicationForm}
                render={({ history, location }) => {
                  return (
                    <>
                      <ShoppingCart
                        history={history}
                        prefill={this.state.prefill}
                        calculation={
                          this.state.prefill?.financialProduct?.calculation
                        }
                        vehicleData={this.state.prefill?.vehicleData}
                      />
                      <DataForm
                        initialValues={this.state.summary || this.state.prefill}
                        initialSection={
                          +(location.state && location.state.section)
                        }
                        isLoading={this.state.fullAppForm.isSavingData}
                        onSubmit={(formValues, actions) => {
                          const transactionId =
                            this.state.prefill.storefrontTransactionId;
                          this.setState({
                            summary: formValues,
                            fullAppForm: { isSavingData: true },
                          });
                          savePaymentDetails(transactionId, formValues)
                            .then(() => {
                              actions.setSubmitting(false);
                              history.push(AppRoute.Summary);
                            })
                            .catch((error) => {
                              console.error(error);
                              history.push(AppRoute.Error, {
                                errorCode:
                                  ErrorCode.FullApplicationFormSavePaymentDetails,
                              });
                            })
                            .finally(() => {
                              this.setState({
                                fullAppForm: { isSavingData: false },
                              });
                            });
                        }}
                        onSectionSubmit={({ values }) => {
                          this.setState({ summary: values });
                        }}
                      >
                        <PersonalDetailsSectionFull
                          title={Output.resolve(
                            "pages.applicationForm.panels.personalData.title"
                          )}
                          onSectionSubmit={(formValues) => {
                            this.setState({
                              fullAppForm: { isSavingData: true },
                            });
                            const transactionId =
                              this.state.prefill.storefrontTransactionId;
                            savePersonalDetailsFullAppForm(
                              transactionId,
                              formValues
                            )
                              .catch((error) => {
                                this.handleError(
                                  error,
                                  history,
                                  ErrorCode.FullApplicationFormSavePersonalDetails
                                );
                              })
                              .finally(() => {
                                this.setState({
                                  fullAppForm: { isSavingData: false },
                                });
                              });
                          }}
                        />
                        <ContactDetailsSectionFull
                          title={Output.resolve(
                            "pages.applicationForm.panels.contactData.title"
                          )}
                          onSectionSubmit={(formValues) => {
                            this.setState({
                              fullAppForm: { isSavingData: true },
                            });
                            if (
                              this.state.summary?.email !== formValues.email
                            ) {
                              this.checkMailAddress(formValues);
                            }
                            const transactionId =
                              this.state.prefill.storefrontTransactionId;

                            saveContactDetailsFullAppForm(
                              transactionId,
                              formValues
                            )
                              .catch((error) => {
                                this.handleError(
                                  error,
                                  history,
                                  ErrorCode.FullApplicationFormSaveContactDetails
                                );
                              })
                              .finally(() => {
                                this.setState({
                                  fullAppForm: { isSavingData: false },
                                });
                              });
                          }}
                        />
                        <EmploymentDetailsSectionFull
                          title={Output.resolve(
                            "pages.applicationForm.panels.employmentData.title"
                          )}
                          onSectionSubmit={(formValues) => {
                            const transactionId =
                              this.state.prefill.storefrontTransactionId;
                            this.setState({
                              fullAppForm: { isSavingData: true },
                            });
                            saveEmploymentDetails(transactionId, formValues)
                              .catch((error) => {
                                this.handleError(
                                  error,
                                  history,
                                  ErrorCode.FullApplicationFormSaveEmploymentDetails
                                );
                              })
                              .finally(() => {
                                this.setState({
                                  fullAppForm: { isSavingData: false },
                                });
                              });
                          }}
                        />
                        <ExpenditureDetailsSectionFull
                          title={Output.resolve(
                            "pages.applicationForm.panels.expenditure.title"
                          )}
                          onSectionSubmit={(formValues) => {
                            const transactionId =
                              this.state.prefill.storefrontTransactionId;
                            this.setState({
                              fullAppForm: { isSavingData: true },
                            });
                            saveExpenditureDetails(transactionId, formValues)
                              .catch((error) => {
                                this.handleError(
                                  error,
                                  history,
                                  ErrorCode.FullApplicationFormSaveExpenditureDetails
                                );
                              })
                              .finally(() => {
                                this.setState({
                                  fullAppForm: { isSavingData: false },
                                });
                              });
                          }}
                        />
                        <PaymentSection
                          title={Output.resolve(
                            "pages.applicationForm.panels.payment.title"
                          )}
                        />
                      </DataForm>
                      <SaveButton
                        onClick={() => {
                          this.setState({
                            showContinueLaterModal: true,
                            onDemand: true,
                          });
                        }}
                      />
                    </>
                  );
                }}
              />
              <Route
                path={AppRoute.DocumentUpload}
                render={({ history }) => {
                  return (
                    <>
                      <ShoppingCart
                        history={history}
                        prefill={this.state.prefill}
                        calculation={
                          this.state.prefill?.financialProduct?.calculation
                        }
                        vehicleData={this.state.prefill?.vehicleData}
                      />
                      {this.state?.uploading && <Spinner fullPage/>}
                      <DocumentUpload
                        inputData={this.state.summary || this.state.prefill}
                        onLoad={() => this.onLoadDocuments(history)}
                        onSuccess={async () => {
                          this.handleSaveCustomerDocs(history);
                        }}
                        transactionId={
                          this.state.prefill.storefrontTransactionId
                        }
                        documents={this.state.documents}
                      />
                      <SaveButton
                        onClick={() => {
                          this.setState({
                            showContinueLaterModal: true,
                            onDemand: true,
                          });
                        }}
                      />
                    </>
                  );
                }}
              />
              <Route
                path={AppRoute.DocumentUploadSuccess}
                render={() => {
                  return (
                    <DocumentUploadSuccessPage
                      redirectUrl={
                        this.state.prefill.salesChannel &&
                        this.state.prefill.salesChannel.redirectUrl
                      }
                      originSystem={
                        this.state.prefill?.salesChannel?.originSystem
                      }
                    />
                  );
                }}
              />
              <Route
                path={AppRoute.Esign}
                render={({ history }) => (
                  <AcceptanceEsign
                    inspectionReportLink={
                      this.state.prefill.vehicleData?.appraisals &&
                      this.state.prefill.vehicleData?.appraisals[0]
                        ? this.state.prefill.vehicleData?.appraisals[0].url
                        : "/#"
                    }
                    originSystem={
                      this.state.prefill?.salesChannel?.originSystem
                    }
                    identificationStarted={this.state.prefill.identificationStarted}
                    onSubmit={async () => {
                      availabilityCheck(
                        this.state.prefill?.storefrontTransactionId
                      ).then(
                        () => {
                          window.location.href = this.state?.prefill?.idNowUrl;
                        },
                        (error) => {
                          this.handleError(error, history);
                        }
                      );
                    }}
                  />
                )}
              />
              <Route
                path={AppRoute.EsignPending}
                render={({ location, history }) => {
                  const identificationId = Optional.of(
                    history.location.search.match(
                      /\?transaction-number=([^?&]+)/
                    )
                  )
                    .map((v) => v && v[1])
                    .getOrUndefined();

                  if (!identificationId) {
                    history.push(AppRoute.Error, {
                      errorCode: ErrorCode.MissingIdentificationId,
                    });
                    return;
                  }

                  return (
                    <IdentityPending
                      addToDatalayer={pushStorefrontDataToDatalayer}
                      identityID={identificationId}
                      onError={(err) => {
                        this.handleError(
                          err,
                          history,
                          ErrorCode.IdentificationPendingError
                        );
                      }}
                      onSuccess={(url, prefill) => {
                        const state = { redirectUrl: url };
                        this.setState({
                          prefill: {
                            ...this.state?.prefill,
                            salesChannel: {
                              originSystem: prefill.salesChannel.originSystem,
                            },
                            financialProduct: {
                              calculation: prefill.financialProduct.calculation,
                            },
                            dealerData: prefill.dealerData,
                            vehicleData: prefill.vehicleData,
                          },
                        });
                        history.push(AppRoute.EsignSuccess, state);
                      }}
                      onFail={(state) => {
                        history.push(AppRoute.EsignFailed, state);
                      }}
                      onLoad={(salesChannel) => {
                        const originSystem = salesChannel?.originSystem;
                        if (originSystem) {
                          this.setState({
                            isDataLoaded: true,
                            prefill: {
                              ...this.state?.prefill,
                              salesChannel: {
                                originSystem: originSystem,
                              },
                            },
                          });
                        }
                      }}
                    />
                  );
                }}
              />
              <Route
                path={AppRoute.EsignSuccess}
                render={({ location, history }) => (
                  <SignatureSuccessPage
                    shoppingCart={
                      <ShoppingCart
                        history={history}
                        prefill={this.state.prefill}
                          calculation={
                            this.state.prefill?.financialProduct?.calculation
                        }
                        additionalServices={
                          this.state.prefill?.financialProduct
                            ?.additionalServicesDescriptions
                            ? this.state.prefill.financialProduct
                                .additionalServicesDescriptions[0]
                            : undefined
                        }
                        vehicleData={this.state.prefill?.vehicleData}
                      />
                    }
                    redirectUrl={
                      location.state?.redirectUrl || // URL returned by the LegitimationPending step, or
                      this.state?.prefill?.transaction?.brandRedirectUrl // URL stored in the LCV state
                    }
                    prefill={this.state?.prefill}
                  />
                )}
              />
              <Route
                path={AppRoute.EsignFailed}
                render={(props) => <IdentFailed {...props} />}
              />
              <Route
                path={AppRoute.Summary}
                render={({ history }) => {
                  return (
                    <>
                      {this.state?.uploading && <Spinner fullPage/>}
                      <ShoppingCart
                        history={history}
                        prefill={this.state.prefill}
                        calculation={
                          this.state.prefill?.financialProduct?.calculation
                        }
                        vehicleData={this.state.prefill?.vehicleData}
                      />
                      <SummaryPage
                        isSavingData={this.state.summaryPage.isSavingData}
                        tlsSupport={
                          this.state.tlsCheck.key ===
                            this.state.summary?.email &&
                          this.state.tlsCheck.value
                        }
                        values={this.state.summary || this.state.prefill}
                        originSystem={
                          this.state.prefill?.salesChannel?.originSystem
                        }
                        vehicleData={this.state.prefill?.vehicleData}
                        dealerData={this.state.prefill?.dealerData}
                        handleEdit={({ page, section }) => {
                          history.push(page, { section });
                        }}
                        onSectionLoaded={() =>
                          this.setState({ isSummary: true })
                        }
                        handleError={(error) => {
                          this.handleError(
                            error,
                            history,
                            ErrorCode.Default
                          );
                        }}
                        prefill={this.state?.prefill}
                        onAdditionalProductSelection={(product) => {
                          let updatedAdditionalProducts = this.state.prefill?.financialProduct?.additionalProducts?.map((p) => {
                            return p.code === product?.code ? {...p, isSelected: !p?.isSelected} : p
                          });
                          this.setState({
                            ...this.state,
                            prefill: {
                              ...this.state.prefill,
                              financialProduct: {
                                ...this.state.prefill?.financialProduct,
                                additionalProducts: updatedAdditionalProducts,
                              }
                            },
                            ...(this.state.summary && { summary: {
                              ...this.state.summary,
                              financialProduct: {
                                ...this.state.summary?.financialProduct,
                                additionalProducts: updatedAdditionalProducts,
                              }
                            }}),
                          })
                        }}
                        onSubmit={data => {
                          const { token, tokenExpiresAt } = data;
                          this.updateSessionData({ token });
                          this.expiredSessionStart(tokenExpiresAt);
                          this.setState({ ...this.state, uploading: true });
                          try {
                            const TIMEOUT_AFTER = 120000;
                            const INTERVAL = 3000;
                            const getIdentStatus = async () => {
                              try {
                                const finishStatusResult = await checkStatus(
                                  this.state.transactionId
                                );
                                switch (finishStatusResult.status) {
                                  case "IN_PROGRESS":
                                    break;
                                  case "SUCCESS_APPROVED":
                                    clearInterval(timer);
                                    clearTimeout(timeoutTimer);
                                    this.afterSummaryPage(true, history);
                                    break;

                                  case "SUCCESS_REFUSED":
                                    clearInterval(timer);
                                    clearTimeout(timeoutTimer);
                                    this.afterSummaryPage(false, history);
                                    break;
                                  default:
                                    clearInterval(timer);
                                    clearTimeout(timeoutTimer);
                                    history.push(AppRoute.Error, {
                                      errorCode: ErrorCode.CreditCheckError,
                                    });
                                    break;
                                }
                              } catch (e) {
                                this.setState({ ...this.state, uploading: false });
                                clearInterval(timer);
                                clearTimeout(timeoutTimer);
                                history.push(AppRoute.Error, {
                                  errorCode: ErrorCode.CreditCheckError,
                                });
                              }
                            };

                            const timeoutTimer = setTimeout(() => {
                              this.setState({ ...this.state, uploading: false });
                              clearInterval(timer);
                              clearTimeout(timeoutTimer);
                              history.push(AppRoute.Error, {
                                errorCode: ErrorCode.CreditCheckError,
                              });
                            }, TIMEOUT_AFTER);
                            const timer = setInterval(
                              getIdentStatus,
                              INTERVAL
                            );
                          }
                          catch(error) {
                            this.setState({ ...this.state, uploading: false });
                            this.handleError(
                              error,
                              history,
                              ErrorCode.FullApplicationFormDocumentUploadError
                            );
                          }
                          this.setState({ isSummary: false });
                        }}
                      />
                    </>
                  );
                }}
              />

              <Route
                path={AppRoute.BoniLanding}
                render={({ history }) => (
                  <BoniLanding
                    transactionId={this.state.prefill.storefrontTransactionId}
                    onContinue={() => {
                      history.push(AppRoute.FullApplicationForm);
                    }}
                    originSystem={
                      this.state.prefill?.salesChannel?.originSystem
                    }
                    vehicleData={this.state.prefill?.vehicleData}
                  />
                )}
              />

              <Route
                path={AppRoute.BoniRefused}
                render={({ location }) => (
                  <BoniRefused
                    redirectUrl={
                      location.state?.redirectUrl ||
                      this.state?.prefill?.salesChannel?.redirectUrl
                    }
                    originSystem={
                      this.state.prefill?.salesChannel?.originSystem
                    }
                  />
                )}
              />

              <Route
                path={AppRoute.BoniSuccessDealer}
                render={({ location }) => (
                  <BoniSuccessDealer
                    redirectUrl={
                      location.state?.redirectUrl ||
                      this.state?.prefill?.salesChannel?.redirectUrl
                    }
                  />
                )}
              />

              <Route
                path={AppRoute.BoniSuccessUCC}
                render={({ history }) => (
                  <BoniSuccessUCC
                    onContinue={() => {
                      history.push(AppRoute.DocumentUpload);
                    }}
                  />
                )}
              />
              {/* Dac is DEPRECATED */}
              <Route
                path={AppRoute.Dac}
                render={({ history }) => {
                  return (
                    <>
                      <ShoppingCart
                        history={history}
                        prefill={this.state.prefill}
                        calculation={
                          this.state.prefill?.financialProduct?.calculation
                        }
                        vehicleData={this.state.prefill?.vehicleData}
                      />

                      <CheckoutDacPage
                        transactionId={
                          this.state.prefill.storefrontTransactionId
                        }
                        productModifier={
                          this.state.prefill.vehicleData?.isUsed
                            ? ProductModifier.USED
                            : ProductModifier.NEW
                        }
                        history={history}
                        onError={(error) => {
                          // GFTDE-30: If the digital account check fails, customer is redirected
                          // to the Additional Application Page as if the customer had chosen the “manual path”.
                          this.handleError(
                            error,
                            history,
                            ErrorCode.DacError,
                            AppRoute.AdditionalAppFormDacError
                          );
                        }}
                      />
                      <SaveButton
                        onClick={() => {
                          this.setState({
                            showContinueLaterModal: true,
                            onDemand: true,
                          });
                        }}
                      />
                    </>
                  );
                }}
              />
              {/* AdditionalAppForm is DEPRECATED */}
              <Route
                path={`${AppRoute.AdditionalAppForm}/:dacFailed?`}
                render={({ history, match }) => {
                  return (
                    <>
                      <ShoppingCart
                        history={history}
                        prefill={this.state.prefill}
                        calculation={
                          this.state.prefill?.financialProduct?.calculation
                        }
                        vehicleData={this.state.prefill?.vehicleData}
                      />
                      <AdditionalAppFormPage
                        dacFailed={match.params.dacFailed}
                        initialValues={this.state.summary || this.state.prefill}
                        productModifier={
                          this.state.prefill.vehicleData?.isUsed
                            ? ProductModifier.USED
                            : ProductModifier.NEW
                        }
                        history={history}
                        onSectionSubmit={({ values }) => {
                          this.setState({
                            summary: {
                              ...this.state.summary,
                              ...values,
                            },
                          });
                        }}
                      />
                      <SaveButton
                        onClick={() => {
                          this.setState({
                            showContinueLaterModal: true,
                            onDemand: true,
                          });
                        }}
                      />
                    </>
                  );
                }}
              />
              {/* SoftCreditApproved is DEPRECATED */}
              <Route
                path={AppRoute.SoftCreditApproved}
                render={({ history }) => {
                  return (
                    <CreditResultPage
                      creditResult={TransactionStatus.SOFTCREDIT_APPROVED}
                      onSuccess={() => {
                        startFullApplication(
                          this.state.prefill.storefrontTransactionId
                        )
                          .then(() => {
                            history.push(AppRoute.FullApplicationForm);
                          })
                          .catch((error) => {
                            this.handleError(
                              error,
                              history,
                              ErrorCode.FullApplicationStartError
                            );
                          });
                      }}
                    />
                  );
                }}
              />
              {/* SoftCreditRefused is DEPRECATED */}
              <Route
                path={AppRoute.SoftCreditRefused}
                render={() => {
                  return (
                    <CreditResultPage
                      creditResult={TransactionStatus.SOFTCREDIT_REFUSED}
                      onSuccess={() => {
                        window.location.href =
                          this.state.prefill.salesChannel &&
                          this.state.prefill.salesChannel.redirectUrl;
                      }}
                    />
                  );
                }}
              />
              <Route
                path={AppRoute.DataProtection}
                component={DataProtectionPage}
              />
              <Route path={AppRoute.Licenses} component={Licenses} />
              <Route
                path={AppRoute.TermsAndConditionsPage}
                component={TermsAndConditionsPage}
              />
              <Route
                path={AppRoute.ExpiredSession}
                render={() => (
                  <ExpiredSessionPage />
                )}
              />
              <Route
                path={AppRoute.Error}
                render={(routeProps) => {
                  return (
                    <ErrorInformation
                      errorCode={routeProps.location.state?.errorCode}
                      routeProps={routeProps}
                      entryPointUrl={
                        this.state.sessionData?.entryPointUrl ||
                        this.state.prefill?.salesChannel?.entryPointUrl
                      }
                    />
                  );
                }}
              />
              <Route
                path={AppRoute.VehicleUnavailable}
                render={(routeProps) => (
                  <VehicleUnavailableError
                    redirectUrl={
                      this.state.prefill?.salesChannel?.redirectUrl ||
                      this.state.sessionData?.redirectUrl
                    }
                    originSystem={
                      this.state.prefill?.salesChannel?.originSystem
                    }
                  />
                )}
              />
            </Switch>
          </Main>
          <BackToTop />
          <Footer
            isSummary={this.state.isSummary}
            originSystem={this.state.prefill?.salesChannel?.originSystem}
          />
        </SessionContext.Provider>
      </Router>
    );
  }
}

export default App;
